import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
  LinearProgress,
  Avatar,
  Tooltip,
} from '@mui/material';
import {
  Visibility,
  Favorite,
  Close,
  CalendarToday,
  LocationOn,
  AttachMoney,
} from '@mui/icons-material';
import { Match, MatchStatus, Property, Client } from '@/types';
import { format } from 'date-fns';

interface MatchCardProps {
  match: Match;
  property?: Property;
  client?: Client;
  onView?: (match: Match) => void;
  onAccept?: (match: Match) => void;
  onReject?: (match: Match) => void;
  onSchedule?: (match: Match) => void;
  showActions?: boolean;
}

const MatchCard: React.FC<MatchCardProps> = ({
  match,
  property,
  client,
  onView,
  onAccept,
  onReject,
  onSchedule,
  showActions = true,
}) => {
  const getStatusColor = (status: MatchStatus) => {
    const colorMap: { [key: string]: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' } = {
      pending: 'default',
      sent: 'primary',
      viewed: 'secondary',
      interested: 'success',
      not_interested: 'error',
      scheduled: 'warning',
      expired: 'default',
    };
    return colorMap[status] || 'default';
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const handleView = () => {
    if (onView) {
      onView(match);
    }
  };

  const handleAccept = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onAccept) {
      onAccept(match);
    }
  };

  const handleReject = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onReject) {
      onReject(match);
    }
  };

  const handleSchedule = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onSchedule) {
      onSchedule(match);
    }
  };

  const propertyTitle = property?.metadata.title || 'Property';
  const propertyPrice = property?.metadata.price
    ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: property.metadata.currency || 'USD',
        minimumFractionDigits: 0,
      }).format(property.metadata.price)
    : 'Price not available';

  const propertyLocation = property?.location
    ? `${property.location.city}, ${property.location.state}`
    : 'Location not available';

  const propertyImage = property?.metadata.images?.[0] || '';

  const clientName = client
    ? `${client.profile.firstName} ${client.profile.lastName}`
    : 'Client';

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: onView ? 'pointer' : 'default',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': onView
          ? {
              transform: 'translateY(-4px)',
              boxShadow: 4,
            }
          : {},
      }}
      onClick={handleView}
    >
      {/* Property Image */}
      {propertyImage && (
        <Box
          sx={{
            width: '100%',
            height: 200,
            backgroundImage: `url(${propertyImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              display: 'flex',
              gap: 1,
            }}
          >
            <Chip
              label={match.status}
              color={getStatusColor(match.status)}
              size="small"
            />
          </Box>
        </Box>
      )}

      <CardContent sx={{ flexGrow: 1 }}>
        {/* Match Score */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" component="h3" noWrap>
              {propertyTitle}
            </Typography>
            <Chip
              label={`${match.score.overall}%`}
              color={getScoreColor(match.score.overall)}
              size="small"
              sx={{ fontWeight: 'bold' }}
            />
          </Box>
          <LinearProgress
            variant="determinate"
            value={match.score.overall}
            color={getScoreColor(match.score.overall)}
            sx={{ height: 8, borderRadius: 1 }}
          />
        </Box>

        {/* Property Details */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: 'text.secondary' }}>
            <AttachMoney fontSize="small" sx={{ mr: 0.5 }} />
            <Typography variant="body2">{propertyPrice}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: 'text.secondary' }}>
            <LocationOn fontSize="small" sx={{ mr: 0.5 }} />
            <Typography variant="body2">{propertyLocation}</Typography>
          </Box>
          {property?.metadata.bedrooms && property.metadata.bathrooms && (
            <Typography variant="body2" color="text.secondary">
              {property.metadata.bedrooms} bed • {property.metadata.bathrooms} bath
              {property.metadata.squareFootage && ` • ${property.metadata.squareFootage.toLocaleString()} sqft`}
            </Typography>
          )}
        </Box>

        {/* Client Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Avatar sx={{ width: 32, height: 32, mr: 1 }}>
            {client?.profile.firstName?.[0] || 'C'}
          </Avatar>
          <Typography variant="body2" color="text.secondary">
            Match for {clientName}
          </Typography>
        </Box>

        {/* Match Strengths */}
        {match.score.strengths && match.score.strengths.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              Strengths:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {match.score.strengths.slice(0, 2).map((strength, index) => (
                <Chip
                  key={index}
                  label={strength}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Match Explanation */}
        {match.score.explanation && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {match.score.explanation}
          </Typography>
        )}

        {/* Created Date */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Matched: {format((match.createdAt as any).toDate(), 'MMM dd, yyyy')}
        </Typography>
      </CardContent>

      {showActions && (
        <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Visibility />}
            onClick={handleView}
          >
            View
          </Button>
          <Box>
            {match.status === MatchStatus.PENDING || match.status === MatchStatus.VIEWED ? (
              <>
                {onAccept && (
                  <Tooltip title="Mark as Interested">
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<Favorite />}
                      onClick={handleAccept}
                      sx={{ mr: 1 }}
                    >
                      Accept
                    </Button>
                  </Tooltip>
                )}
                {onReject && (
                  <Tooltip title="Not Interested">
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<Close />}
                      onClick={handleReject}
                    >
                      Reject
                    </Button>
                  </Tooltip>
                )}
              </>
            ) : match.status === MatchStatus.INTERESTED && onSchedule ? (
              <Tooltip title="Schedule Viewing">
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  startIcon={<CalendarToday />}
                  onClick={handleSchedule}
                >
                  Schedule
                </Button>
              </Tooltip>
            ) : null}
          </Box>
        </CardActions>
      )}
    </Card>
  );
};

export default MatchCard;

